import React, { useState } from 'react';
import { Button, Dropdown, Image, Input, message, Popconfirm, Spin, Upload } from 'antd';
import type { MenuProps } from 'antd';
import { DownOutlined, MoreOutlined } from '@ant-design/icons';
import type { FilmLocationZone, ImageModelInfo } from '../../types/film';
import { useBankVars } from './useBankVars';
import { categoryLabel, PROP_CATEGORY_OPTIONS } from './propCardUtils';
import { apiErrorText } from './apiErrorText';
import ZonesEditor from './ZonesEditor'; // 261010 P2-1
import WfIconButton from './WfIconButton'; // UI-assettabs
import WfIcon from './WfIcon';
import { LOCATION_LABELS } from './assetCardsUi';
import './bank.css';
import './assetCards.css';

/**
 * Thẻ bối cảnh / đạo cụ (đợt 260922 E2-P3) — cùng ngôn ngữ với thẻ nhân vật, theo mockup đã duyệt và thẻ TopView:
 * ảnh nguyên tấm → hàng nút `Gen lại ▾ · Tải ảnh · Prompt` → tên → mô tả (sửa tại chỗ).
 *
 * `Gen lại` bấm chính = ĐƯỜNG CŨ (`generateLocationRef` / `generatePropSheet`) — không đổi hành vi. Menu ▾ = gen qua
 * Xưởng ảnh bằng model khác rồi dùng luôn (nghĩa "Regenerate" của TopView). Không hiện giá (User 23/09: *"bỏ hết giá"*).
 */
export type AssetKind = 'location' | 'prop';
export type AssetField = 'name' | 'description' | 'spatialLayout' | 'visualDescription' | 'category';

export interface AssetLike {
  id: string;
  name: string;
  description?: string | null;
  canonicalRefUrl?: string | null;
  spatialLayout?: string | null;
  /** 260922 E4-P1 — chỉ bối cảnh: đoạn tả sạch (ô "Visual description"); `description` thành khối "Nguồn từ kịch bản". */
  visualDescription?: string | null;
  /** 260922 E4-P2 — chỉ đạo cụ: loại (bảng đóng kiểu TopView); `null`/thiếu = chưa phân loại. */
  category?: string | null;
  /** 260922 E4-P3 — chỉ đạo cụ: tên gọi khác của CÙNG vật. */
  aliases?: string[] | null;
  /** 261010 P2-1 — chỉ bối cảnh: zone (kiến trúc tĩnh của nơi), người sửa tay. */
  zones?: FilmLocationZone[] | null;
}

export interface FilmAssetCardProps {
  kind: AssetKind;
  item: AssetLike;
  /** Danh mục model của Xưởng ảnh — menu ▾ chỉ ghi TÊN. */
  models: ImageModelInfo[];
  /** Việc đang chạy trên thẻ này — khoá nút tương ứng. */
  busy?: 'gen' | 'geo' | 'upload' | 'delete' | 'suggest' | 'visual' | 'merge' | null;
  onSaveField: (field: AssetField, value: string) => Promise<void>;
  onGen: () => Promise<void>;
  /** `undefined` = model mặc định của Xưởng ảnh cho loại này (server tự chọn). */
  onGenWithModel: (modelId?: string) => Promise<void>;
  onUpload: (file: File) => Promise<void>;
  onOpenStudio: () => void;
  onDelete: () => void;
  /** Chỉ bối cảnh: AI viết bố cục không gian. */
  onGenGeometry?: () => Promise<void>;
  /** Chỉ đạo cụ: AI viết mô tả khi còn trống. */
  onSuggestDescription?: () => Promise<void>;
  /** 260922 E4-P3 — chỉ đạo cụ: lưu TOÀN BỘ danh sách tên gọi khác (thêm/xoá đều gửi lại cả mảng). */
  onSaveAliases?: (list: string[]) => Promise<void>;
  /** 260922 E4-P1 — chỉ bối cảnh: AI viết (lại) Visual Description. */
  onGenVisual?: () => Promise<void>;
  /** 261010 P2-1 — chỉ bối cảnh: lưu TOÀN BỘ danh sách zone. Có prop này thì thẻ hiện ô "Khu vực (zone)". */
  onSaveZones?: (zones: FilmLocationZone[]) => Promise<void>;
  /** Cảnh báo không chặn của lượt AI viết gần nhất (thời điểm / ánh sáng / người / độ dài). */
  visualWarnings?: string[];
  /** UI-assettabs — khung bước Tài sản: thẻ kiểu bảng (ảnh · tên · nút biểu tượng · ⋯). Không truyền ⇒ thẻ Bank cũ nguyên. */
  board?: boolean;
  /** Chỉ với `board` + bối cảnh (panel đầy bề rộng): vẽ MỘT mảnh của thẻ thay vì cả thẻ — `actions` = nút tạo/tải ảnh đè lên ảnh chuẩn · `menu` = ⋯ ở đầu panel · `info` = mô tả/bố cục/khu vực ở chân panel. */
  part?: 'actions' | 'menu' | 'info';
}

const LABELS: Record<AssetKind, { what: string; desc: string; descHint: string }> = {
  location: { what: 'bối cảnh', desc: LOCATION_LABELS.desc, descHint: 'Tả không gian: kiến trúc, vật liệu, đồ đạc cố định, ánh sáng' },
  prop: { what: 'đạo cụ', desc: 'Mô tả hình dáng', descHint: 'Màu + chất liệu + kích thước + chi tiết riêng — đừng tả hành động' },
};

// Mảng rỗng ỔN ĐỊNH: ZonesEditor reset bản nháp khi `zones` đổi tham chiếu — `|| []` mỗi lần render sẽ gây vòng lặp.
const NO_ZONES: FilmLocationZone[] = [];

const FilmAssetCard: React.FC<FilmAssetCardProps> = (p) => {
  const vars = useBankVars();
  const it = p.item;
  const L = LABELS[p.kind];
  const [editing, setEditing] = useState<AssetField | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState<AssetField | null>(null);
  // Mô tả bối cảnh cũ có thể mang cả khối trích kịch bản (`buildLocationDescription`) — gọn 6 dòng, bấm mới bung.
  const [more, setMore] = useState<AssetField | null>(null);
  const hasImg = !!it.canonicalRefUrl;
  const genBusy = p.busy === 'gen';
  // 260922 E4-P3 — "Tên gọi khác" (chỉ đạo cụ): thêm tay qua ô nhập inline, xoá qua nút ✕ trên chip.
  const [addingAlias, setAddingAlias] = useState(false);
  const [aliasDraft, setAliasDraft] = useState('');
  const [savingAlias, setSavingAlias] = useState(false);
  const aliases = it.aliases || [];
  const saveAliases = async (list: string[]) => {
    if (!p.onSaveAliases) return;
    setSavingAlias(true);
    try { await p.onSaveAliases(list); }
    catch (e: any) { message.error(apiErrorText(e, 'Lưu tên gọi khác')); }
    finally { setSavingAlias(false); }
  };
  const addAlias = async () => {
    const v = aliasDraft.trim();
    setAddingAlias(false);
    setAliasDraft('');
    if (!v) return;
    await saveAliases([...aliases, v]);
  };
  const removeAlias = (a: string) => saveAliases(aliases.filter((x) => x !== a));

  const startEdit = (f: AssetField, v: string) => { setEditing(f); setDraft(v); };
  const cancel = () => { setEditing(null); setDraft(''); };
  const commit = async () => {
    const f = editing;
    if (!f) return;
    const v = draft.trim();
    setEditing(null);
    const cur = (f === 'name' ? it.name : f === 'description' ? it.description : f === 'visualDescription' ? it.visualDescription : it.spatialLayout) || '';
    if (v === cur.trim() || (f === 'name' && !v)) return;
    setSaving(f);
    try { await p.onSaveField(f, v); } finally { setSaving(null); }
  };
  // Chip loại đạo cụ — chọn từ menu, không qua ô sửa tại chỗ như các field còn lại.
  const pickCategory = async (value: string) => {
    if (value === (it.category || '')) return;
    setSaving('category');
    try { await p.onSaveField('category', value); } finally { setSaving(null); }
  };
  const editor = (multiline: boolean, placeholder?: string) => (multiline
    ? <Input.TextArea autoFocus autoSize={{ minRows: 2, maxRows: 10 }} size="small" value={draft} placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Escape') cancel(); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); } }} />
    : <Input autoFocus size="small" value={draft} placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit} onPressEnter={commit}
        onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }} />);

  // Menu ▾: model mặc định của loại (server chọn) + từng model theo TÊN.
  const modelItems: MenuProps['items'] = [
    { key: 'h', label: 'Gen bằng model khác — ảnh mới thay ảnh đang dùng', disabled: true },
    { type: 'divider' },
    { key: '__default', label: `Mặc định của Xưởng ảnh cho ${L.what}`, disabled: genBusy },
    ...p.models.map((m) => ({ key: m.id, label: m.label, disabled: genBusy })),
  ];
  const onModel: MenuProps['onClick'] = ({ key }) => {
    if (key === 'h') return;
    p.onGenWithModel(key === '__default' ? undefined : String(key));
  };

  const genMain = hasImg
    ? (
      <Popconfirm title={`Gen lại ảnh "${it.name}"?`} description="Ảnh đang dùng sẽ bị thay." okText="Gen lại" cancelText="Thôi" onConfirm={() => p.onGen()}>
        <Button size="small" type="primary" loading={genBusy}>Gen lại</Button>
      </Popconfirm>
    )
    : <Button size="small" type="primary" loading={genBusy} onClick={() => p.onGen()}>Gen ảnh</Button>;

  const menu: MenuProps['items'] = [
    { key: 'delete', danger: true, label: `Xoá ${L.what}`, onClick: () => p.onDelete() },
  ];

  // 260922 E4-P1: ô mô tả của BỐI CẢNH là Visual Description; `description` (nguồn từ kịch bản) xuống khối thu gọn bên dưới.
  const isLoc = p.kind === 'location';
  const descField: AssetField = isLoc ? 'visualDescription' : 'description';
  const desc = ((isLoc ? it.visualDescription : it.description) || '').trim();
  const source = isLoc ? (it.description || '').trim() : '';
  const [showSource, setShowSource] = useState(false);
  const geo = (it.spatialLayout || '').trim();

  // 260922 E4-P2 — chỉ đạo cụ: chip loại, bấm ⇒ menu bảng đóng + "Bỏ trống".
  const categoryMenu: MenuProps['items'] = [
    ...PROP_CATEGORY_OPTIONS.map((c) => ({ key: c, label: categoryLabel(c) })),
    { type: 'divider' as const },
    { key: '', label: 'Bỏ trống' },
  ];

  // ── UI-assettabs: thẻ kiểu bảng (khung bước Tài sản) ──
  if (p.board) {
    const clampOf = (f: AssetField) => (more === f ? '' : ' clamp');
    const moreBtn = (f: AssetField, text: string) => (text.length > 150 && editing !== f ? (
      <button type="button" className="ac-lnk" onClick={() => setMore(more === f ? null : f)}>{more === f ? 'Thu gọn' : 'Xem hết'}</button>
    ) : null);
    const aiDescBusy = isLoc ? p.busy === 'visual' : p.busy === 'suggest';
    const aiDesc = isLoc ? p.onGenVisual : p.onSuggestDescription;
    const modelMenuItems: MenuProps['items'] = p.models.length ? [{
      key: 'models', label: 'Tạo bằng model khác', disabled: genBusy,
      children: [
        { key: '__default', label: `Mặc định của Xưởng ảnh cho ${L.what}`, disabled: genBusy, onClick: () => { void p.onGenWithModel(undefined); } },
        ...p.models.map((m) => ({ key: m.id, label: m.label, disabled: genBusy, onClick: () => { void p.onGenWithModel(m.id); } })),
      ],
    }] : [];
    const boardMenu: MenuProps['items'] = [
      { key: 'custom', label: 'Custom Prompt…', onClick: () => p.onOpenStudio() },
      ...modelMenuItems,
      { type: 'divider' as const },
      ...(menu || []),
    ];
    const genBtn = hasImg ? (
      <Popconfirm title={`Tạo lại ảnh "${it.name}"?`} description="Ảnh đang dùng sẽ bị thay." okText="Tạo lại" cancelText="Thôi" onConfirm={() => p.onGen()}>
        <span className="wf-ib-w"><WfIconButton icon="reload" label="Tạo lại ảnh" loading={genBusy} testId="ac-gen" /></span>
      </Popconfirm>
    ) : <WfIconButton icon="picture" label="Tạo ảnh" loading={genBusy} onClick={() => p.onGen()} testId="ac-gen" />;
    if (p.part === 'actions') {
      return (
        <>
          {genBtn}
          <Upload accept="image/*" showUploadList={false} customRequest={({ file }) => { p.onUpload(file as File); }}>
            <WfIconButton icon="upload" label="Tải ảnh lên" loading={p.busy === 'upload'} testId="ac-upload" />
          </Upload>
        </>
      );
    }
    if (p.part === 'menu') {
      return (
        <Dropdown menu={{ items: boardMenu }} trigger={['click']}>
          <span className="wf-ib-w"><WfIconButton icon="ellipsis" label="Thêm" loading={p.busy === 'delete'} testId="ac-more" /></span>
        </Dropdown>
      );
    }
    if (p.part === 'info') {
      const wide = more === descField;
      return (
        <div className="ac-info" data-testid="ac-loc-info" data-location-id={it.id}>
          <div className="ac-line">
            <b>Mô tả:</b>
            {editing === descField ? <span className="tx">{editor(true, L.descHint)}</span>
              : <span className={`tx${wide ? '' : ' one'}${desc ? '' : ' miss'}`} title={wide ? undefined : desc}>
                  {saving === descField || (aiDescBusy && !desc) ? <Spin size="small" /> : (desc || LOCATION_LABELS.emptyDesc)}
                </span>}
            {desc.length > 90 && editing !== descField ? <button type="button" className="ac-lnk" onClick={() => setMore(wide ? null : descField)}>{wide ? 'Thu gọn' : 'Xem hết'}</button> : null}
            {editing !== descField ? <WfIconButton icon="edit" label="Sửa mô tả" onClick={() => startEdit(descField, desc)} /> : null}
            {aiDesc ? (desc ? (
              <Popconfirm title="AI viết lại mô tả hình ảnh?" description="Bản đang có sẽ bị thay." okText="Viết lại" cancelText="Thôi" onConfirm={() => aiDesc()}>
                <span className="wf-ib-w"><WfIconButton icon="bulb" label={LOCATION_LABELS.aiRewrite} loading={aiDescBusy} /></span>
              </Popconfirm>
            ) : <WfIconButton icon="bulb" label={LOCATION_LABELS.aiWrite} loading={aiDescBusy} onClick={() => aiDesc()} />) : null}
          </div>
          {(p.visualWarnings || []).map((w) => <div key={w} className="ac-note">{w}</div>)}
          {source ? (
            <details className="ac-fold"><summary>{LOCATION_LABELS.source}</summary>
              <p className="ac-desc" style={{ cursor: 'default' }} title="AI dùng để viết mô tả hình ảnh và bố cục không gian — không đưa thẳng vào ảnh / clip">{source}</p>
            </details>
          ) : null}
          <details className="ac-fold" data-testid="ac-loc-layout">
            <summary>{LOCATION_LABELS.layout}</summary>
            <div className="ac-line" style={{ marginTop: 6 }}>
              {editing === 'spatialLayout' ? <span className="tx">{editor(true, 'Cửa / cửa sổ / đồ đạc lớn và vị trí của chúng')}</span>
                : <div className={`ac-desc${geo ? '' : ' miss'}${clampOf('spatialLayout')}`} style={{ flex: 1 }} onClick={() => startEdit('spatialLayout', geo)}>
                    {saving === 'spatialLayout' ? <Spin size="small" /> : (geo || 'Chưa có — bấm để viết, hoặc để AI viết')}
                  </div>}
              {p.onGenGeometry ? <WfIconButton icon="bulb" label={geo ? 'AI viết lại bố cục' : 'AI viết bố cục'} loading={p.busy === 'geo'} onClick={() => p.onGenGeometry!()} /> : null}
            </div>
            {moreBtn('spatialLayout', geo)}
          </details>
          {p.onSaveZones ? (
            <ZonesEditor zones={it.zones || NO_ZONES} onSave={async (z) => {
              try { await p.onSaveZones!(z); message.success('Đã lưu khu vực'); }
              catch (e: any) { message.error(apiErrorText(e, 'Lưu khu vực')); }
            }} />
          ) : null}
        </div>
      );
    }
    return (
      <div className="ac-card" style={vars} data-testid={`ac-${p.kind}-card`}>
        <div className={`bk-sheet ${p.kind === 'prop' ? 'prop' : 'wide'}${hasImg ? ' has' : ''}`}>
          {hasImg
            ? <Image className="main" src={it.canonicalRefUrl!} alt={it.name} width="100%" height="100%" style={{ objectFit: 'contain' }} />
            : <div className="empty">Chưa có ảnh {L.what}{genBtn}</div>}
          {(genBusy || p.busy === 'upload') && hasImg && <div className="empty" style={{ background: 'rgba(0,0,0,.35)' }}><Spin /></div>}
        </div>
        <div className="ac-body">
          {editing === 'name' ? editor(false, `Tên ${L.what}`)
            : <h3 className="ac-name" title={it.name} onClick={() => startEdit('name', it.name)}>{saving === 'name' ? <Spin size="small" /> : it.name}</h3>}
          {!isLoc && (
            <div className="ac-sub">
              <Dropdown trigger={['click']} menu={{ items: categoryMenu, onClick: ({ key }) => pickCategory(String(key)) }}>
                <button type="button" className={`ac-cat${it.category ? '' : ' miss'}`} disabled={saving === 'category'}>
                  {saving === 'category' ? <Spin size="small" /> : categoryLabel(it.category)}
                </button>
              </Dropdown>
              {aliases.length > 0 ? <span className="t" style={{ cursor: 'default' }} title={`Cũng gọi: ${aliases.join(', ')}`}>cũng gọi: {aliases.join(', ')}</span> : null}
            </div>
          )}
          <div className="ac-desc-h ac-sub" style={{ justifyContent: 'space-between' }}>
            <b>{L.desc}</b>
            {desc && aiDesc ? (
              <Popconfirm title={isLoc ? 'AI viết lại mô tả hình ảnh?' : 'AI viết lại mô tả?'} description="Bản đang có sẽ bị thay." okText="Viết lại" cancelText="Thôi" onConfirm={() => aiDesc()}>
                <span className="wf-ib-w"><WfIconButton icon="bulb" label={LOCATION_LABELS.aiRewrite} loading={aiDescBusy} /></span>
              </Popconfirm>
            ) : null}
          </div>
          {editing === descField ? editor(true, L.descHint)
            : (
              <div className={`ac-desc${desc ? '' : ' miss'}${clampOf(descField)}`} onClick={() => startEdit(descField, desc)}>
                {saving === descField || (aiDescBusy && !desc) ? <Spin size="small" /> : (desc || (isLoc ? LOCATION_LABELS.emptyDesc : 'Chưa có mô tả — bấm để thêm'))}
              </div>
            )}
          {moreBtn(descField, desc)}
          {!desc && aiDesc && editing !== descField ? (
            <button type="button" className="wf-btn" style={{ alignSelf: 'flex-start' }} disabled={aiDescBusy} onClick={() => aiDesc()}><WfIcon name="bulb" />{LOCATION_LABELS.aiWrite}</button>
          ) : null}
          {(p.visualWarnings || []).map((w) => <div key={w} className="ac-note">{w}</div>)}
          {isLoc && source ? (
            <>
              <button type="button" className="ac-lnk" onClick={() => setShowSource((v) => !v)}>{showSource ? 'Ẩn' : 'Xem'} {LOCATION_LABELS.source.toLowerCase()}</button>
              {showSource ? <p className="ac-desc" style={{ cursor: 'default' }} title="AI dùng để viết mô tả hình ảnh và bố cục không gian — không đưa thẳng vào ảnh / clip">{source}</p> : null}
            </>
          ) : null}
          {isLoc ? (
            <>
              <div className="ac-desc-h ac-sub" style={{ justifyContent: 'space-between' }}>
                <b>{LOCATION_LABELS.layout}</b>
                {p.onGenGeometry ? (
                  <WfIconButton icon="bulb" label={geo ? 'AI viết lại bố cục' : 'AI viết bố cục'} loading={p.busy === 'geo'} onClick={() => p.onGenGeometry!()} />
                ) : null}
              </div>
              {editing === 'spatialLayout' ? editor(true, 'Cửa / cửa sổ / đồ đạc lớn và vị trí của chúng')
                : (
                  <div className={`ac-desc${geo ? '' : ' miss'}${clampOf('spatialLayout')}`} onClick={() => startEdit('spatialLayout', geo)}>
                    {saving === 'spatialLayout' ? <Spin size="small" /> : (geo || 'Chưa có — bấm để viết, hoặc để AI viết')}
                  </div>
                )}
              {moreBtn('spatialLayout', geo)}
              {p.onSaveZones ? (
                <ZonesEditor zones={it.zones || NO_ZONES} onSave={async (z) => {
                  try { await p.onSaveZones!(z); message.success('Đã lưu khu vực'); }
                  catch (e: any) { message.error(apiErrorText(e, 'Lưu khu vực')); }
                }} />
              ) : null}
            </>
          ) : null}
          {!isLoc && p.onSaveAliases ? (
            <div className="bk-chips" style={{ alignItems: 'center' }}>
              {aliases.map((a) => (
                <span className="bk-chip" key={a}>{a}
                  <button type="button" aria-label={`Xoá tên gọi khác "${a}"`} onClick={() => removeAlias(a)} disabled={savingAlias}
                    style={{ border: 0, background: 'none', cursor: 'pointer', marginLeft: 4, padding: 0, color: 'inherit' }}>✕</button>
                </span>
              ))}
              {savingAlias && <Spin size="small" />}
              {addingAlias ? (
                <Input size="small" autoFocus style={{ width: 140 }} value={aliasDraft} placeholder="Tên gọi khác" onChange={(e) => setAliasDraft(e.target.value)} onBlur={addAlias}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setAddingAlias(false); setAliasDraft(''); } if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }} />
              ) : (
                <WfIconButton icon="plus" label="Thêm tên gọi khác" onClick={() => setAddingAlias(true)} disabled={savingAlias} />
              )}
            </div>
          ) : null}
        </div>
        <div className="ac-acts">
          {genBtn}
          <Upload accept="image/*" showUploadList={false} customRequest={({ file }) => { p.onUpload(file as File); }}>
            <WfIconButton icon="upload" label="Tải ảnh lên" loading={p.busy === 'upload'} testId="ac-upload" />
          </Upload>
          <span className="sp" />
          <Dropdown menu={{ items: boardMenu }} trigger={['click']}>
            <span className="wf-ib-w"><WfIconButton icon="ellipsis" label="Thêm" loading={p.busy === 'delete'} testId="ac-more" /></span>
          </Dropdown>
        </div>
      </div>
    );
  }

  return (
    <div className="bk-card" style={vars}>
      <div className={`bk-sheet ${p.kind === 'prop' ? 'prop' : 'wide'}${hasImg ? ' has' : ''}`}>
        {hasImg
          ? <Image className="main" src={it.canonicalRefUrl!} alt={it.name} width="100%" height="100%" style={{ objectFit: 'contain' }} />
          : <div className="empty">Chưa có ảnh {L.what}{genMain}</div>}
        {(genBusy || p.busy === 'upload') && hasImg && <div className="empty" style={{ background: 'rgba(0,0,0,.35)' }}><Spin /></div>}
      </div>

      <div className="bk-acts">
        <span className="bk-split">
          {genMain}
          {/* Không `disabled` cả Dropdown: khoá lúc menu đang mở làm menu kẹt mở (chạy thử 23/09) ⇒ khoá từng mục. */}
          <Dropdown trigger={['click']} menu={{ items: modelItems, onClick: onModel }}>
            <Button size="small" type="primary" icon={<DownOutlined />} aria-label="Chọn model" />
          </Dropdown>
        </span>
        <Upload accept="image/*" showUploadList={false} customRequest={({ file }) => { p.onUpload(file as File); }}>
          <Button size="small" loading={p.busy === 'upload'}>Tải ảnh lên</Button>
        </Upload>
        <Button size="small" onClick={p.onOpenStudio}>Custom Prompt</Button>
      </div>

      <div className="bk-body">
        <div className="bk-name">
          {editing === 'name' ? editor(false, `Tên ${L.what}`)
            : <h3 onClick={() => startEdit('name', it.name)}>{saving === 'name' ? <Spin size="small" /> : it.name}</h3>}
          {!isLoc && (
            <Dropdown trigger={['click']} menu={{ items: categoryMenu, onClick: ({ key }) => pickCategory(String(key)) }}>
              <button type="button" className={`bk-chip ${it.category ? 'ok' : ''}`} disabled={saving === 'category'}>
                {saving === 'category' ? <Spin size="small" /> : categoryLabel(it.category)}
              </button>
            </Dropdown>
          )}
          {!hasImg && <span className="bk-cnt none">chưa có ảnh</span>}
          <Dropdown menu={{ items: menu }} trigger={['click']}>
            <Button className="more" size="small" type="text" icon={<MoreOutlined />} loading={p.busy === 'delete'} />
          </Dropdown>
        </div>

        <div className="bk-lbl">
          {L.desc}
          <span style={{ flex: 1 }} />
          {isLoc && desc && p.onGenVisual && (
            <Popconfirm title="AI viết lại mô tả hình ảnh?" description="Bản đang có sẽ bị thay." okText="Viết lại" cancelText="Thôi" onConfirm={() => p.onGenVisual!()}>
              <button type="button" className="bk-lnk" disabled={p.busy === 'visual'}>{p.busy === 'visual' ? <Spin size="small" /> : 'AI viết lại'}</button>
            </Popconfirm>
          )}
          {!isLoc && desc && p.onSuggestDescription && (
            <Popconfirm title="AI viết lại mô tả?" description="Bản đang có sẽ bị thay." okText="Viết lại" cancelText="Thôi" onConfirm={() => p.onSuggestDescription!()}>
              <button type="button" className="bk-lnk" disabled={p.busy === 'suggest'}>{p.busy === 'suggest' ? <Spin size="small" /> : 'AI viết lại'}</button>
            </Popconfirm>
          )}
        </div>
        {editing === descField ? editor(true, L.descHint)
          : (
            <div className={`bk-desc ${desc ? '' : 'miss'} ${more === descField ? '' : 'clamp'}`} onClick={() => startEdit(descField, desc)}>
              {saving === descField || (isLoc && p.busy === 'visual' && !desc) ? <Spin size="small" /> : (desc || (isLoc ? LOCATION_LABELS.emptyDesc : 'Chưa có mô tả — bấm để thêm'))}
            </div>
          )}
        {desc.length > 320 && editing !== descField && (
          <button type="button" className="bk-lnk" style={{ alignSelf: 'flex-start' }} onClick={() => setMore(more === descField ? null : descField)}>
            {more === descField ? 'Thu gọn' : 'Xem hết'}
          </button>
        )}
        {!desc && !isLoc && p.onSuggestDescription && editing !== 'description' && (
          <div><Button size="small" loading={p.busy === 'suggest'} onClick={() => p.onSuggestDescription!()}>✨ AI viết mô tả</Button></div>
        )}
        {!desc && isLoc && p.onGenVisual && editing !== descField && (
          <div><Button size="small" loading={p.busy === 'visual'} onClick={() => p.onGenVisual!()}>✨ {LOCATION_LABELS.aiWrite}</Button></div>
        )}
        {(p.visualWarnings || []).map((w) => <div key={w} className="bk-note" style={{ borderLeftColor: 'var(--bk-warn)' }}>{w}</div>)}
        {isLoc && source && (
          <>
            <button type="button" className="bk-lnk" style={{ alignSelf: 'flex-start' }} onClick={() => setShowSource((v) => !v)}>
              {showSource ? '▾' : '▸'} {LOCATION_LABELS.source}
            </button>
            {showSource && (
              <div className="bk-desc" style={{ cursor: 'default', whiteSpace: 'pre-wrap' }} title="AI dùng để viết mô tả hình ảnh và bố cục không gian — không đưa thẳng vào ảnh / clip">
                {source}
              </div>
            )}
          </>
        )}

        {p.kind === 'prop' && p.onSaveAliases && (
          <>
            <div className="bk-lbl">Tên gọi khác</div>
            <div className="bk-chips" style={{ alignItems: 'center' }}>
              {aliases.map((a) => (
                <span className="bk-chip" key={a}>
                  {a}
                  <button
                    type="button"
                    aria-label={`Xoá tên gọi khác "${a}"`}
                    onClick={() => removeAlias(a)}
                    disabled={savingAlias}
                    style={{ border: 0, background: 'none', cursor: 'pointer', marginLeft: 4, padding: 0, color: 'inherit' }}
                  >✕</button>
                </span>
              ))}
              {!aliases.length && !addingAlias && (
                <span style={{ fontSize: 11, color: 'var(--bk-muted)' }}>
                  Chưa có tên gọi khác — thêm khi kịch bản gọi món này bằng tên khác
                </span>
              )}
              {savingAlias && <Spin size="small" />}
              {addingAlias ? (
                <Input
                  size="small" autoFocus style={{ width: 140 }} value={aliasDraft}
                  onChange={(e) => setAliasDraft(e.target.value)}
                  onBlur={addAlias}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { setAddingAlias(false); setAliasDraft(''); }
                    if (e.key === 'Enter') { e.preventDefault(); addAlias(); }
                  }}
                />
              ) : (
                <button type="button" className="bk-chip" onClick={() => setAddingAlias(true)} disabled={savingAlias}>+</button>
              )}
            </div>
          </>
        )}

        {p.kind === 'location' && (
          <>
            <div className="bk-lbl">
              {LOCATION_LABELS.layout}
              <span style={{ flex: 1 }} />
              {p.onGenGeometry && (
                <button type="button" className="bk-lnk" disabled={p.busy === 'geo'} onClick={() => p.onGenGeometry!()}>
                  {p.busy === 'geo' ? <Spin size="small" /> : geo ? 'AI viết lại' : 'AI viết'}
                </button>
              )}
            </div>
            {editing === 'spatialLayout' ? editor(true, 'Cửa / cửa sổ / đồ đạc lớn và vị trí của chúng')
              : (
                <div className={`bk-desc ${geo ? '' : 'miss'} ${more === 'spatialLayout' ? '' : 'clamp'}`} onClick={() => startEdit('spatialLayout', geo)}>
                  {saving === 'spatialLayout' ? <Spin size="small" /> : (geo || 'Chưa có — bấm để viết, hoặc để AI viết')}
                </div>
              )}
            {geo.length > 320 && editing !== 'spatialLayout' && (
              <button type="button" className="bk-lnk" style={{ alignSelf: 'flex-start' }} onClick={() => setMore(more === 'spatialLayout' ? null : 'spatialLayout')}>
                {more === 'spatialLayout' ? 'Thu gọn' : 'Xem hết'}
              </button>
            )}
            {p.onSaveZones && (
              <ZonesEditor zones={it.zones || NO_ZONES} onSave={async (z) => {
                try { await p.onSaveZones!(z); message.success('Đã lưu zone'); }
                catch (e: any) { message.error(apiErrorText(e, 'Lưu zone')); }
              }} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FilmAssetCard;
